import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportarError } from "@/shared/errors/report";

interface Props {
  children: ReactNode;
}

interface State {
  reventó: boolean;
}

/**
 * La red de seguridad de React.
 *
 * Cuando un componente lanza al pintar, React DESMONTA el árbol entero: sin
 * esto, quien lo vive se queda mirando una pantalla en blanco, sin mensaje,
 * sin botón y sin manera de contarnos qué pasó. Con esto, ve una salida — y
 * nosotros vemos el error en el log el mismo minuto.
 *
 * Tiene que ser una clase: los ganchos no tienen equivalente a
 * `componentDidCatch`. Es la única clase del frontend, y es por eso.
 *
 * ── Por qué el aviso va con estilos en línea ─────────────────────────
 * Esta pantalla se pinta JUSTO DESPUÉS de que algo reventó, y lo que
 * reventó puede ser el propio sistema de diseño, la hoja de estilos que no
 * cargó o un chunk que no bajó. Un fallback que importa `Button`,
 * `PageContainer` y Tailwind es un fallback que revienta con la misma
 * causa que vino a atrapar, y entonces la pantalla en blanco vuelve. Sin
 * dependencias: HTML y estilos en el atributo. Es la única pantalla del
 * proyecto donde eso es lo correcto.
 *
 * ── Por qué el botón RECARGA en vez de "reintentar" ──────────────────
 * Un `setState({ reventó: false })` volvería a pintar el mismo árbol con el
 * mismo estado que lo rompió: se rompe otra vez, en bucle. Recargar es lo
 * único que garantiza un intento limpio, y de paso trae la versión nueva
 * del frontend si el error fue un chunk viejo tras un despliegue —que es
 * la causa más común de esta pantalla—.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { reventó: false };

  static getDerivedStateFromError(): State {
    return { reventó: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * `componentStack` es lo que convierte este reporte en accionable: dice
     * QUÉ componente reventó, algo que el stack de JavaScript minificado no
     * dice. Va pegado al stack real, no en un campo aparte, para que viaje
     * dentro del techo que ya negocia el contrato.
     */
    const conComponente = new Error(error.message);
    conComponente.stack = `${error.stack ?? ""}\n--- componentes ---${info.componentStack ?? ""}`;
    reportarError(conComponente, "render");
  }

  override render(): ReactNode {
    if (!this.state.reventó) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          background: "#ffffff",
          color: "#1a1a1a",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, margin: 0 }}>
          Algo se rompió en esta pantalla
        </h1>
        <p style={{ maxWidth: "32rem", margin: 0, lineHeight: 1.6, color: "#555" }}>
          No fue culpa tuya. Ya nos llegó el aviso con el detalle. Vuelve a cargar la
          página; si sigue igual, escríbenos y lo revisamos.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: "0.5rem",
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            fontWeight: 600,
            color: "#ffffff",
            background: "#1a1a1a",
            border: "none",
            borderRadius: "9999px",
            cursor: "pointer",
          }}
        >
          Volver a cargar
        </button>
      </div>
    );
  }
}
